import { useEffect, useState } from 'react'
import { splashImages } from '../assets/images'

const splashBg = splashImages.bg
const splashText = splashImages.text
const splashSparkle = splashImages.sparkle
const splashPeach = splashImages.peachShine

interface SplashScreenProps {
  onComplete: () => void
}

export default function SplashScreen({ onComplete }: SplashScreenProps) {
  const [peachVisible, setPeachVisible] = useState(false)

  useEffect(() => {
    // Start fade-in for peach after a brief moment
    const fadeTimer = setTimeout(() => {
      setPeachVisible(true)
    }, 50)

    // Complete splash after 3 seconds
    const completeTimer = setTimeout(() => {
      onComplete()
    }, 3000)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(completeTimer)
    }
  }, [onComplete])

  return (
    <div style={styles.container}>
      {/* Inject keyframes animation */}
      <style>{sparkleKeyframes}</style>

      <div style={styles.imageStack}>
        {/* Background layer */}
        <img
          src={splashBg}
          alt=""
          style={styles.bgImage}
        />
        {/* Text layer (always visible) */}
        <img
          src={splashText}
          alt=""
          style={styles.textImage}
        />
        {/* Debug: visualize the mask (hidden) */}
        {/* <div style={styles.maskDebug} /> */}
        {/* Sparkle layer with animated mask */}
        <img
          src={splashSparkle}
          alt=""
          style={styles.sparkleImage}
        />
        {/* Peach shine layer - fades in over 0.5s */}
        <img
          src={splashPeach}
          alt=""
          style={{
            ...styles.peachImage,
            opacity: peachVisible ? 1 : 0
          }}
        />
      </div>
    </div>
  )
}

// CSS keyframes for the rolling wave sparkle effect and peach wiggle
const sparkleKeyframes = `
  @keyframes sparkleWave {
    0% {
      background-position: 0% 50%;
      mask-position: 0% 50%;
      -webkit-mask-position: 0% 50%;
    }
    100% {
      background-position: 100% 50%;
      mask-position: 100% 50%;
      -webkit-mask-position: 100% 50%;
    }
  }

  @keyframes peachWiggle {
    0%, 100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(1.5deg);
    }
    75% {
      transform: rotate(-1.5deg);
    }
  }
`

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    overflow: 'hidden'
  },
  imageStack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  },
  bgImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center'
  },
  textImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center'
  },
  maskDebug: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    background: `linear-gradient(
      -20deg,
      transparent 0%,
      transparent 32%,
      rgba(255,255,255,0.8) 34%,
      rgba(255,255,255,0.8) 36%,
      transparent 38%,
      transparent 40%,
      rgba(255,255,255,0.8) 42%,
      rgba(255,255,255,0.8) 44%,
      transparent 46%,
      transparent 49%,
      rgba(255,255,255,0.8) 51%,
      rgba(255,255,255,0.8) 53%,
      transparent 55%,
      transparent 57%,
      rgba(255,255,255,0.8) 59%,
      rgba(255,255,255,0.8) 61%,
      transparent 63%,
      transparent 100%
    )`,
    backgroundSize: '300% 100%',
    animation: 'sparkleWave 3s linear infinite alternate',
    zIndex: 100
  },
  sparkleImage: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    objectFit: 'cover',
    objectPosition: 'top center',
    filter: 'brightness(1.3) contrast(1.2) drop-shadow(0 0 8px rgba(255,255,255,0.8)) drop-shadow(0 0 15px rgba(255,200,255,0.6))',
    maskImage: `linear-gradient(
      -20deg,
      transparent 0%,
      transparent 32%,
      black 34%,
      black 36%,
      transparent 38%,
      transparent 40%,
      black 42%,
      black 44%,
      transparent 46%,
      transparent 49%,
      black 51%,
      black 53%,
      transparent 55%,
      transparent 57%,
      black 59%,
      black 61%,
      transparent 63%,
      transparent 100%
    )`,
    WebkitMaskImage: `linear-gradient(
      -20deg,
      transparent 0%,
      transparent 32%,
      black 34%,
      black 36%,
      transparent 38%,
      transparent 40%,
      black 42%,
      black 44%,
      transparent 46%,
      transparent 49%,
      black 51%,
      black 53%,
      transparent 55%,
      transparent 57%,
      black 59%,
      black 61%,
      transparent 63%,
      transparent 100%
    )`,
    maskSize: '300% 100%',
    WebkitMaskSize: '300% 100%',
    animation: 'sparkleWave 3s linear infinite alternate'
  } as React.CSSProperties,
  peachImage: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    objectFit: 'cover',
    objectPosition: 'top center',
    transition: 'opacity 0.5s ease-in',
    // Peach center at 1309x3734 from bottom-left on 2513x5463 image = 52.09% from left, 31.65% from top
    transformOrigin: '52.09% 31.65%',
    animation: 'peachWiggle 2s ease-in-out infinite'
  } as React.CSSProperties
}
